import { AppDispatch, speechToken, speechTokenApiSlice } from '@circuitry-ai/doc-data';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { renderToStaticMarkup } from 'react-dom/server';
import remarkGfm from 'remark-gfm';
import { azureSupportedLanguages } from './constants';

export class SpeechUtility {
    private static instance: SpeechUtility | undefined;
    private static tokenObj: speechToken = { token: '', region: '' };
    private static currentSynthesizer: speechsdk.SpeechSynthesizer | undefined;
    private readonly transrecognizer: speechsdk.TranslationRecognizer;
    private readonly transspeechConfig: speechsdk.SpeechTranslationConfig;
    private readonly recognizer: speechsdk.SpeechRecognizer;
    private readonly speechConfig: speechsdk.SpeechConfig;
    private myPlayer: speechsdk.SpeakerAudioDestination | undefined;
    private static language = 'en-US';
    public static languages: { [key: string]: string; } = azureSupportedLanguages;
    public static translateLanguages: { [key: string]: string; } = azureSupportedLanguages;
    // public static readonly languages: { [key: string]: string } = { "en-US": "English", "te-IN": "Telugu", "es-ES": "Spanish" };

    public static mapLanguages(newLanguages: string[]): { [key: string]: string } {
        return Object.entries(azureSupportedLanguages)
            .filter(([code, name]) => newLanguages.includes(name))
            .reduce((acc, [code, name]) => {
                acc[code] = name;
                return acc;
            }, {} as { [key: string]: string });
    }

    public static setLanguage(language: string) {
        SpeechUtility.language = language;
    }

    public static getLanguage() {
        return SpeechUtility.language;
    }
    public static updateTranslationLanguages(newLanguages: string[]) {
        const mappedLanguages = SpeechUtility.mapLanguages(newLanguages);
        SpeechUtility.translateLanguages = { ...mappedLanguages };
    }
    private constructor() {
        this.speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(SpeechUtility.tokenObj.token, SpeechUtility.tokenObj.region);
        this.speechConfig.speechRecognitionLanguage = SpeechUtility.language;
        const sttaudioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
        this.recognizer = new speechsdk.SpeechRecognizer(this.speechConfig, sttaudioConfig);
        this.transspeechConfig = speechsdk.SpeechTranslationConfig.fromAuthorizationToken(SpeechUtility.tokenObj.token, SpeechUtility.tokenObj.region);
        this.transspeechConfig.speechRecognitionLanguage = SpeechUtility.language;
        this.transspeechConfig.addTargetLanguage("en");
        this.transrecognizer = new speechsdk.TranslationRecognizer(this.transspeechConfig, sttaudioConfig);
    }


    public static async getInstance(dispatch: AppDispatch): Promise<SpeechUtility> {
        if (!SpeechUtility.instance) {
            await (async () => {
                const { data } = await dispatch(speechTokenApiSlice.endpoints.getToken.initiate());
                if (data) SpeechUtility.tokenObj = data;
            })();
            SpeechUtility.instance = new SpeechUtility();
        }
        return SpeechUtility.instance;
    }

    public static destroyInstance() {
        if (SpeechUtility.instance) {
            SpeechUtility.instance.recognizer.close();
            SpeechUtility.instance = undefined;
        }
    }

    public static stopRecognition() {
        if (!SpeechUtility.instance) return;
        SpeechUtility.instance.recognizer.stopContinuousRecognitionAsync();
        SpeechUtility.instance.transrecognizer.stopContinuousRecognitionAsync();
    }

    public static stopSpeech() {
        if (SpeechUtility.currentSynthesizer) {
            try {
                SpeechUtility.currentSynthesizer.close();
            } catch {
                // Ignore if already disposed
            } finally {
                SpeechUtility.currentSynthesizer = undefined;
            }
        }
        if (SpeechUtility.instance?.myPlayer) {
            try {
                SpeechUtility.instance.myPlayer.pause();
            } catch {
                // Ignore if already disposed
            } finally {
                SpeechUtility.instance.myPlayer = undefined;
            }
        }
    }

    async sttFromMic(isListening: boolean, setIsListening: (isListening: boolean) => void, inputValue: string, setInputValue: (inputValue: string) => void) {
        const isTranslating = SpeechUtility.language !== 'en-US';
        if (isListening) {
            (isTranslating ? this.transrecognizer : this.recognizer).stopContinuousRecognitionAsync();
            setIsListening(false);
            return;
        }
        let silenceTimeoutId: NodeJS.Timeout, promptInput = inputValue ?? '', lastRecognizedPhrase = '', newRecognizedText = '';
        (isTranslating ? this.transrecognizer : this.recognizer).recognizing = (s: any, e: any) => {
            newRecognizedText = e.result.text.replace(lastRecognizedPhrase, '').trim();
            lastRecognizedPhrase = e.result.text;
            promptInput = `${promptInput} ${newRecognizedText}`;
            setInputValue(promptInput ?? '');

            // Clear the previous timeout and set a new one
            clearTimeout(silenceTimeoutId);
            silenceTimeoutId = setTimeout(() => {
                (isTranslating ? this.transrecognizer : this.recognizer).stopContinuousRecognitionAsync();
                setIsListening(false);
            }, 2000); // 2 seconds of silence
        };

        (isTranslating ? this.transrecognizer : this.recognizer).recognized = (s: any, e: any) => {
            if (e.result.text) {
                setInputValue(`${inputValue ?? ''} ${e.result.text}`.trim());
            }
        };

        (isTranslating ? this.transrecognizer : this.recognizer).canceled = (s: any, e: any) => {
            setInputValue('ERROR: Speech was cancelled or could not be recognized. Ensure your microphone is working properly.');
        };

        (isTranslating ? this.transrecognizer : this.recognizer).startContinuousRecognitionAsync();
        setIsListening(true);
    }

    async recognizeOnce(): Promise<string> {
        const isTranslating = SpeechUtility.language !== 'en-US';
        const recognizer = isTranslating ? this.transrecognizer : this.recognizer;
        return new Promise((resolve, reject) => {
            recognizer.recognizeOnceAsync(
                result => {
                    resolve(result?.text ?? '');
                },
                err => {
                    const message = err?.errorDetails || err?.message || 'Speech recognition failed.';
                    reject(new Error(message));
                }
            );
        });
    }

    async recognizeOnceWithSilence(
        timeoutMs: number = 2000,
        startTimeoutMs: number = 5000,
        onPartial?: (text: string) => void
    ): Promise<string> {
        const isTranslating = SpeechUtility.language !== 'en-US';
        const recognizer = isTranslating ? this.transrecognizer : this.recognizer;
        return new Promise((resolve, reject) => {
            let silenceTimeoutId: NodeJS.Timeout;
            let startedTimeoutId: NodeJS.Timeout;
            const finalSegments: string[] = [];
            let lastPartial = '';
            let lastFinal = '';
            let isResolved = false;
            let hasSpeech = false;
            const toError = (err: any) =>
                new Error(err?.errorDetails || err?.message || 'Speech recognition failed.');

            const stopAndResolve = () => {
                if (isResolved) return;
                isResolved = true;
                clearTimeout(silenceTimeoutId);
                clearTimeout(startedTimeoutId);
                recognizer.stopContinuousRecognitionAsync();
                const segments = [...finalSegments];
                if (lastPartial && segments[segments.length - 1] !== lastPartial) {
                    segments.push(lastPartial);
                }
                resolve(segments.join(' ').trim());
            };

            recognizer.recognizing = (_s: any, e: any) => {
                if (e?.result?.text) {
                    hasSpeech = true;
                    lastPartial = e.result.text;
                    if (onPartial) onPartial(lastPartial);
                    clearTimeout(startedTimeoutId);
                    clearTimeout(silenceTimeoutId);
                    silenceTimeoutId = setTimeout(() => {
                        stopAndResolve();
                    }, timeoutMs);
                }
            };

            recognizer.recognized = (_s: any, e: any) => {
                if (e?.result?.text) {
                    hasSpeech = true;
                    const text = e.result.text;
                    if (text !== lastFinal && finalSegments[finalSegments.length - 1] !== text) {
                        finalSegments.push(text);
                    }
                    lastFinal = text;
                    lastPartial = '';
                    clearTimeout(startedTimeoutId);
                    clearTimeout(silenceTimeoutId);
                    silenceTimeoutId = setTimeout(() => {
                        stopAndResolve();
                    }, timeoutMs);
                }
            };

            recognizer.canceled = (_s: any, e: any) => {
                if (isResolved) return;
                clearTimeout(silenceTimeoutId);
                clearTimeout(startedTimeoutId);
                reject(toError(e));
            };

            clearTimeout(startedTimeoutId);
            startedTimeoutId = setTimeout(() => {
                if (!hasSpeech) {
                    stopAndResolve();
                }
            }, startTimeoutMs);

            recognizer.startContinuousRecognitionAsync(
                () => undefined,
                err => {
                    clearTimeout(silenceTimeoutId);
                    clearTimeout(startedTimeoutId);
                    reject(toError(err));
                }
            );
        });
    }

    async startContinuousRecognition({
        onPartial,
        onFinal,
        onError,
        translateToEnglish = false
    }: {
        onPartial?: (text: string) => void;
        onFinal?: (text: string) => void;
        onError?: (error: Error) => void;
        translateToEnglish?: boolean;
    }) {
        const isTranslating = SpeechUtility.language !== 'en-US';
        const recognizer = isTranslating ? this.transrecognizer : this.recognizer;
        recognizer.recognizing = (_s: any, e: any) => {
            if (!onPartial) return;
            const text = e?.result?.text ?? '';
            if (text) onPartial(text);
        };
        recognizer.recognized = (_s: any, e: any) => {
            if (!onFinal) return;
            const baseText = e?.result?.text ?? '';
            if (!baseText) return;
            if (translateToEnglish && isTranslating) {
                const translated =
                    e?.result?.translations?.get?.('en') ??
                    e?.result?.translations?.get?.('en-US') ??
                    baseText;
                onFinal(translated);
                return;
            }
            onFinal(baseText);
        };
        recognizer.canceled = (_s: any, e: any) => {
            if (onError) {
                const message = e?.errorDetails || e?.message || 'Speech recognition failed.';
                onError(new Error(message));
            }
        };
        recognizer.startContinuousRecognitionAsync(
            () => undefined,
            err => {
                if (onError) {
                    const message = err?.errorDetails || err?.message || 'Speech recognition failed.';
                    onError(new Error(message));
                }
            }
        );
    }

    async textToSpeech(textToSpeak: string, setIsReading: (isReading: boolean) => void, setIsSpeechMode?: (isSpeechMode: boolean) => void) {
        const normalizedText = this.markdownToPlainText(textToSpeak);
        setIsReading(true);
        SpeechUtility.stopSpeech();
        this.myPlayer = new speechsdk.SpeakerAudioDestination();
        this.myPlayer.onAudioEnd = () => {
            setIsReading(false);
            setIsSpeechMode && setIsSpeechMode(false);
            this.myPlayer = undefined;
            SpeechUtility.currentSynthesizer = undefined;
        };
        const ttsaudioConfig = speechsdk.AudioConfig.fromSpeakerOutput(this.myPlayer);
        const synthesizer = new speechsdk.SpeechSynthesizer(this.speechConfig, ttsaudioConfig);
        SpeechUtility.currentSynthesizer = synthesizer;
        if (SpeechUtility.language !== 'en-US') {
            textToSpeak = await this.translateText(normalizedText, SpeechUtility.language);
        } else {
            textToSpeak = normalizedText;
        }
        synthesizer.speakTextAsync(
            textToSpeak,
            result => {
                synthesizer.close();
            },
            err => {
                synthesizer.close();
            });
    }

    /**
     * This function converts markdown text to plain text by rendering the markdown to HTML and then parsing the HTML to get the text content.
     * @param markdownText - The markdown text to convert to plain text
     * @returns The plain text
     */
    private markdownToPlainText(markdownText: string): string {
        const markdownElement = React.createElement(
            ReactMarkdown as React.ElementType,
            { remarkPlugins: [remarkGfm] },
            markdownText ?? ''
        );
        const html = renderToStaticMarkup(
            markdownElement as any
        );

        if (typeof DOMParser !== 'undefined') {
            const parser = new DOMParser();
            const parsedDocument = parser.parseFromString(html, 'text/html');
            const text =(parsedDocument.body.textContent ?? ''); // textContent is the text of the markdown
            return text.replace(/\s+/g, ' ').trim();
        }

        if (typeof document !== 'undefined') {
            const container = document.createElement('div');
            container.innerHTML = html;
            return (container.textContent ?? '').replace(/\s+/g, ' ').trim();
        }

        // Non-DOM fallback (should be rare in this utility flow)
        return html.replace(/\s+/g, ' ').trim();
    }

    async handleMute(isReading: boolean, setIsReading: (isReading: boolean) => void) {
        if (isReading) {
            this.myPlayer?.pause();
            setIsReading(false);
        } else {
            this.myPlayer?.resume();
            setIsReading(true);
        }
    }

    async translateText(textToTranslate: string, targetLanguage: string): Promise<string> {
        const endpoint = `https://api.cognitive.microsofttranslator.com/translate?api-version=3.0&to=${targetLanguage}`;
        const headers = {
            'Ocp-Apim-Subscription-Key': '5229590d9c6343d98c81ccaf8ad877c0',
            'Ocp-Apim-Subscription-Region': 'global',
            'Content-type': 'application/json'
        };
        const body = JSON.stringify([{ 'text': textToTranslate }]);

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: headers,
                body: body
            });
            const data = await response.json();
            const translations = data[0].translations;
            return translations[0].text;
        } catch (error) {
            throw new Error('Translation failed: ' + error);
        }
    }

}
